import { openNotification } from '@/common/helpers';
import { NotificationTypes } from '@/common/interfaces';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { UploadDocument } from '@/store/api/types.api';
import { userApi } from '@/store/api/user.api';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import React, { FC, useCallback, useEffect } from 'react';

import {
  DocTypeClass,
  DocUploadedUser,
  UserStatus,
  WorksheetData,
} from '../Registration.interfaces';
import { downloadFile } from './Documents.helpers';
import styles from './Documents.module.scss';
import { ModalDelete } from './ModalDelete';
import { ModalFinishForm } from './ModalFinishForm';
import { ModalUpload } from './ModalUpload';
import { TemplatesDoc } from './TemplatesDoc';
import { UploadedDoc } from './UploadedDoc';

type Props = {
  nextStep: () => void;
  prevStep: () => void;
  disableEdit: boolean;
};

export const Documents: FC<Props> = ({ nextStep, prevStep, disableEdit }) => {
  const [activeDocument, setActiveDocument] =
    React.useState<null | DocUploadedUser>(null);

  const [isOpenModalSendFile, setOpenModalSendFile] =
    React.useState<boolean>(false);

  const [isOpenModalDeleteFile, setOpenModalDeleteFile] =
    React.useState<boolean>(false);

  const [isOpenSentModal, setOpenSentModal] = React.useState<boolean>(false);

  const { userInfo, documents } = useTypedSelector(
    (state) => state.registration,
  );

  const [getUserInfo, { isLoading: isLoadingGetUserInfo }] =
    userApi.useLazyGetUserInfoQuery();

  // Получение файла по ID
  const [getFileBase64, { isLoading: isLoadingDownload }] =
    userApi.useGetFileBase64Mutation();

  // Удаления файла по ID
  const [deleteFile, { isLoading: isLoadingDelete }] =
    userApi.useDeleteFileMutation();

  // Отправка файла
  const [uploadFile, { isLoading: loadingUploadFile }] =
    userApi.useUploadFileMutation();

  const [updateUserInfo, { isLoading: loadingUpdateUser }] =
    userApi.useUpdateUserInfoMutation();

  const handleDownloadFile = async (fileId: string) => {
    try {
      const res = await getFileBase64({
        fileId: fileId,
      });

      if (res.data) {
        downloadFile(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openModalSendFile = () => {
    setOpenModalSendFile(true);
  };

  const closeModalSendFile = () => {
    setActiveDocument(null);
    setOpenModalSendFile(false);
  };

  const openModalDeleteFile = () => {
    setOpenModalDeleteFile(true);
  };

  const openModalFinishForm = () => {
    setOpenSentModal(true);
  };

  const closeModalFinishForm = () => {
    setOpenSentModal(false);
  };

  const closeModalDeleteFile = () => {
    setActiveDocument(null);
    setOpenModalDeleteFile(false);
  };

  const submitDeleteFile = async (fileId: string) => {
    try {
      await deleteFile({ fileId: fileId }).unwrap();
      // await getUserInfo();
      closeModalDeleteFile();
      openNotification(NotificationTypes.SUCCESS, 'Файл удален с сервера');
    } catch (e) {
      console.error(e);
      openNotification(
        NotificationTypes.ERROR,
        `Произошла ошибка при попытке удалить файл: ${e}`,
      );
    }
  };

  const submitUploadFile = async (data: UploadDocument) => {
    await uploadFile(data).unwrap();
  };

  const defineActiveDoc = (fileId: string) => {
    const document = userInfo?.docs?.find(
      (doc: DocUploadedUser) => doc.fileId === fileId,
    );
    if (document) {
      setActiveDocument(document);
    }
  };

  const onFinishForm = async () => {
    try {
      const updateUserRequest: WorksheetData = {
        // fullNameRu: userInfo?.fullNameRu,
        // fullNameEn: userInfo?.fullNameEn,
        // shortNameEn: userInfo?.shortNameEn,
        // shortNameRu: userInfo?.shortNameRu,
        // countryId: userInfo?.countryId,
        // inn: userInfo?.inn,
        // regNumber: userInfo?.regNumber,
        // regDate: userInfo?.regDate,
        // registrationAuthorityRu: userInfo?.registrationAuthorityRu,
        // registrationAuthorityEn: userInfo?.registrationAuthorityEn,
        // addressRu: userInfo?.addressRu,
        // addressEn: userInfo?.addressEn,
        // eMail: userInfo?.eMail,
        // phone: userInfo?.phone,
        // basisRuGC: userInfo?.basisRuGC,
        // basisEn: userInfo?.basisEn,
        // signatoryRu: userInfo?.signatoryRu,
        // signatoryRuGC: userInfo?.signatoryRuGC,
        // signatoryEn: userInfo?.signatoryEn,
        // signatoryPositionRu: userInfo?.signatoryPositionRu,
        // signatoryPositionRuGC: userInfo?.signatoryPositionRuGC,
        // signatoryPositionEn: userInfo?.signatoryPositionEn,
        status: UserStatus.CONFIRMATION,
      };
      await updateUserInfo(updateUserRequest);
      closeModalFinishForm();
      openNotification(
        NotificationTypes.SUCCESS,
        'Документы отправлены на проверку',
      );
    } catch (e) {
      console.error(e);
      openNotification(NotificationTypes.ERROR, 'Ошибка отправки формы');
    }
  };

  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  return (
    <>
      <div>
        <h3 className={styles.title}>Регистрационные документы</h3>

        {userInfo?.status === UserStatus.CONFIRMATION && (
          <Alert
            message="Документы отправлены на проверку. Скоро наш менеджер сообщит Вам о результатах проверки."
            type="warning"
            className={styles['alert-registration']}
          />
        )}

        <div className={styles['docs-required']}>
          <h4 className={styles.subtitle}>Перечень необходимых документов</h4>

          <ul className={styles['docs-required__list']}>
            {documents.map((doc) => {
              if (doc.docTypeClass === DocTypeClass.USUAL) {
                return (
                  <li key={doc.id} className={styles.doc}>
                    {doc.name} <br />
                    <span className={styles['doc-description']}>
                      {doc.description && doc.description}
                    </span>
                  </li>
                );
              } else {
                return null;
              }
            })}
          </ul>
        </div>

        {/* Доступные шаблоны документов */}
        <TemplatesDoc
          isLoading={isLoadingDownload}
          getFileBase64={getFileBase64}
        />

        <h4 className={styles.subtitle}>Добавленные документы</h4>

        {isLoadingGetUserInfo ? (
          <div>
            <LoadingOutlined />
          </div>
        ) : (
          <div className={styles['docs-user']}>
            {userInfo?.docs.map((doc: DocUploadedUser) => {
              if (doc.docTypeClass === DocTypeClass.USUAL) {
                return (
                  <UploadedDoc
                    onOpenDeleteModal={openModalDeleteFile}
                    onSetActiveDoc={defineActiveDoc}
                    onDownload={handleDownloadFile}
                    key={doc.fileId}
                    doc={doc}
                    disableEdit={disableEdit}
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        <Button
          style={{ width: '100%' }}
          className={styles['btn-add']}
          color="primary"
          variant="outlined"
          onClick={openModalSendFile}
          icon={<PlusOutlined />}
          disabled={disableEdit}
        >
          Добавить документ
        </Button>

        <div className={styles['steps-buttons']}>
          <Button
            style={{ width: '102px' }}
            htmlType="button"
            onClick={prevStep}
            type="default"
            size="large"
            disabled={disableEdit}
          >
            Назад
          </Button>

          <Button
            htmlType="button"
            type="primary"
            size="large"
            onClick={openModalFinishForm}
            disabled={disableEdit}
          >
            Отправить на проверку
          </Button>
        </div>
      </div>

      <ModalUpload
        onDeleteFile={submitDeleteFile}
        loadingSend={loadingUploadFile || isLoadingGetUserInfo}
        onSubmit={submitUploadFile}
        activeDocument={activeDocument}
        onClose={closeModalSendFile}
        isOpen={isOpenModalSendFile}
      />

      <ModalDelete
        activeDocument={activeDocument}
        isLoadingDelete={isLoadingDelete || isLoadingGetUserInfo}
        onSubmit={submitDeleteFile}
        name="документ"
        onClose={closeModalDeleteFile}
        isOpen={isOpenModalDeleteFile}
      />

      <ModalFinishForm
        onClose={closeModalFinishForm}
        isOpen={isOpenSentModal}
        onFinishForm={onFinishForm}
        updateUserInfoLoading={loadingUpdateUser}
      />
    </>
  );
};
